import { X, Calendar, MapPin, Trash2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCartStore, useCurrencyStore, useLanguageStore } from '../../lib/store'
import { formatPrice, daysBetween, formatDate } from '../../lib/utils'

export default function CartSidebar() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { items, isOpen, closeCart, removeItem } = useCartStore()
    const { currency } = useCurrencyStore()
    const { language } = useLanguageStore()

    const calculateItemTotal = (item: typeof items[0]) => {
        const days = daysBetween(item.startDate, item.endDate)
        const vehicleTotal = item.vehicle.dailyRate * days
        const addonsTotal = item.addons.reduce((sum, addon) => {
            const addonData = item.vehicle.addons.find((a) => a.id === addon.addonId)
            return sum + (addonData?.price || 0) * days
        }, 0)
        return vehicleTotal + addonsTotal
    }

    const calculateTotal = () => {
        return items.reduce((total, item) => total + calculateItemTotal(item), 0)
    }

    const handleCheckout = () => {
        closeCart()
        navigate('/checkout')
    }

    if (!isOpen) return null

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={closeCart}
            />

            {/* Sidebar */}
            <div className="fixed top-0 right-0 h-full w-full md:w-96 bg-white shadow-2xl z-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-primary-600 text-white">
                    <h2 className="text-xl font-bold">{language === 'en' ? 'Bookings' : 'Đặt Xe'}</h2>
                    <button
                        onClick={closeCart}
                        className="p-2 hover:bg-primary-700 rounded-lg transition"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Booking Items */}
                <div className="flex-1 overflow-y-auto p-4">
                    {items.length === 0 ? (
                        <div className="text-center py-12">
                            <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                            <p className="text-gray-500">{t('cart_empty')}</p>
                            <p className="text-sm text-gray-400 mt-2">{language === 'en' ? 'Select a vehicle and dates to book' : 'Chọn xe và ngày để đặt'}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {items.map((item) => {
                                const days = daysBetween(item.startDate, item.endDate)
                                const itemTotal = calculateItemTotal(item)
                                const translation = item.vehicle.translations[language]

                                return (
                                    <div key={item.vehicle.id} className="card p-4 border-2 border-primary-100">
                                        {/* Vehicle Info */}
                                        <div className="flex space-x-3 mb-3">
                                            {item.vehicle.media[0] && (
                                                <img
                                                    src={item.vehicle.media[0].url}
                                                    alt={translation.title}
                                                    className="w-24 h-20 object-cover rounded-lg"
                                                />
                                            )}
                                            <div className="flex-1">
                                                <h3 className="font-semibold line-clamp-2">
                                                    {translation.title}
                                                </h3>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {item.vehicle.brand} {item.vehicle.model}
                                                </p>
                                                <p className="text-sm text-primary-600 font-semibold mt-1 whitespace-nowrap">
                                                    {formatPrice(item.vehicle.dailyRate, currency)}/{language === 'en' ? 'day' : 'ngày'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => removeItem(item.vehicle.id)}
                                                className="text-red-500 hover:text-red-700 self-start"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>

                                        {/* Booking Dates */}
                                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                            <div className="flex items-center text-sm mb-2">
                                                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                                                <span className="font-medium">{language === 'en' ? 'Rental Period:' : 'Thời gian thuê:'}</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                <div>
                                                    <span className="text-gray-500">Từ:</span>
                                                    <p className="font-medium">
                                                        {formatDate(
                                                            (typeof item.startDate === 'string' ? new Date(item.startDate) : item.startDate).getTime() / 1000,
                                                            language
                                                        )}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Đến:</span>
                                                    <p className="font-medium">
                                                        {formatDate(
                                                            (typeof item.endDate === 'string' ? new Date(item.endDate) : item.endDate).getTime() / 1000,
                                                            language
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-xs text-primary-600 mt-2 font-medium">
                                                {days} {language === 'en' ? (days === 1 ? 'day' : 'days') : 'ngày'}
                                            </p>
                                        </div>

                                        {/* Pickup Location */}
                                        {item.pickupLocation && (
                                            <div className="flex items-center text-sm text-gray-600 mb-3">
                                                <MapPin className="w-4 h-4 mr-2" />
                                                <span>{item.pickupLocation}</span>
                                            </div>
                                        )}

                                        {/* Add-ons */}
                                        {item.addons.length > 0 && (
                                            <div className="border-t pt-3">
                                                <p className="text-xs font-semibold text-gray-700 mb-2">{language === 'en' ? 'Add-ons:' : 'Dịch vụ thêm:'}</p>
                                                <div className="space-y-1">
                                                    {item.addons.map((addon) => {
                                                        const addonData = item.vehicle.addons.find((a) => a.id === addon.addonId)
                                                        if (!addonData) return null
                                                        const addonTranslation = addonData.translations[language]

                                                        return (
                                                            <div key={addon.addonId} className="flex items-center justify-between text-xs">
                                                                <span className="flex items-center">
                                                                    <Plus className="w-3 h-3 mr-1 text-green-600" />
                                                                    {addonTranslation.name}
                                                                </span>
                                                                <span className="text-gray-600">
                                                                    +{formatPrice(addonData.price * days, currency)}
                                                                </span>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Item Total */}
                                        <div className="border-t pt-3 mt-3 flex justify-between items-center">
                                            <span className="font-medium">{language === 'en' ? 'Subtotal:' : 'Thành tiền:'}</span>
                                            <span className="text-xl font-bold text-primary-600">
                                                {formatPrice(itemTotal, currency)}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div className="border-t p-4 bg-gray-50 space-y-4">
                        {/* Summary */}
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">{language === 'en' ? 'Vehicles:' : 'Số xe đặt:'}</span>
                                <span className="font-medium">{items.length}</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold">
                                <span>{language === 'en' ? 'TOTAL:' : 'TỔNG CỘNG:'}</span>
                                <span className="text-primary-600">{formatPrice(calculateTotal(), currency)}</span>
                            </div>
                        </div>

                        {/* Escrow Notice */}
                        <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
                            <p className="font-semibold mb-1">🛡️ {language === 'en' ? 'Secure Payment' : 'Thanh toán an toàn'}</p>
                            <p>{language === 'en' ? 'Funds are held in escrow and only released to the owner after rental is complete.' : 'Tiền sẽ được giữ trong escrow và chỉ chuyển cho chủ xe sau khi hoàn thành thuê.'}</p>
                        </div>

                        {/* Checkout Button */}
                        <button
                            onClick={handleCheckout}
                            className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold text-lg transition shadow-lg"
                        >
                            {language === 'en' ? 'Proceed to Checkout' : 'Tiến Hành Đặt Xe'}
                        </button>
                    </div>
                )}
            </div>
        </>
    )
}
